'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Gamepad2, Zap, Trophy, Settings, Palette, Home } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const NAV_ITEMS = [
  { label: 'Home', href: '/', icon: Home },
  { label: 'Play', href: '/', icon: Gamepad2 },
  { label: 'Live Arena', href: '/live-arena', icon: Zap },
  { label: 'Weekly', href: '/weekly-leaderboard', icon: Trophy },
  { label: 'Skins', href: '/customization', icon: Palette },
  { label: 'Settings', href: '/settings', icon: Settings },
];

export default function Topbar() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 h-14 glass-panel-dark border-b border-border flex items-center px-4 lg:px-8">
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <AppLogo size={32} />
            <span className="font-sans font-700 text-base tracking-tight neon-glow-cyan text-primary hidden sm:block"
              style={{ fontWeight: 700 }}>
              LunoraArena
            </span>
          </div>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1 ml-8">
          {NAV_ITEMS?.map((item) => {
            const isActive = pathname === item?.href || (item?.href !== '/' && pathname?.startsWith(item?.href));
            const Icon = item?.icon;
            return (
              <Link
                key={`nav-${item?.href}`}
                href={item?.href}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-primary/10 text-primary border border-primary/30' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon size={14} />
                {item?.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1.5 px-3 py-1 rounded border border-border text-xs font-mono text-muted-foreground">
            <span className="w-1.5 h-1.5 rounded-full bg-neon-green animate-pulse inline-block" />
            <span>Arena Online</span>
          </div>
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
        </div>
      </header>
      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[100] flex">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative ml-auto w-72 h-full glass-panel-dark border-l border-border flex flex-col p-6">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-2">
                <AppLogo size={28} />
                <span className="font-sans font-bold text-sm text-primary neon-glow-cyan">LunoraArena</span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1.5 rounded text-muted-foreground hover:text-foreground"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV_ITEMS?.map((item) => {
                const isActive = pathname === item?.href || (item?.href !== '/' && pathname?.startsWith(item?.href));
                const Icon = item?.icon;
                return (
                  <Link
                    key={`mobile-nav-${item?.href}`}
                    href={item?.href}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-150 ${
                      isActive
                        ? 'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                    }`}
                  >
                    <Icon size={16} />
                    {item?.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}
    </>
  );
}